import type { Editor } from '@tiptap/react';
import type { RxDocument } from 'rxdb';

import { buildGenerateTextVariables, serializeUserSources } from '@/hooks/llm/prompt-invocation-builders';
import { capture } from '@/analytics';
import { isAbortError } from '@/hooks/api';
import type { Block, Note, UserSource } from '@flintmint/core-shared/rxdb';
import type { GenerationUsageMessageMetadata } from '@flintmint/core-shared/message-metadata';
import { getBlockRangeBoundaries, getEditorHTML } from '@/utils/editor-utils';

import {
  commitReviewOutcome,
  createRunGuard,
  finalizeSuccessfulAgentRun,
  prepareReview,
  type AgentLifecycleRuntime,
  type CompletionResolution,
} from '../run/finalize-run';
import { emitAgentRunFinished } from '../run/agent-run-finish-notifier';
import type { PreparedAgentAttempt } from '../run/prepare-run';
import type { AgentRunEvent } from '../run/agent-run-model';
import { assertDocumentFitsTextBudget, ContentTooLargeError } from '../run/content-size-check';
import {
  clearAgentRunEditSnapshot,
  clearRunScrollCleanup,
  getAgentRunEditScope,
  getAgentRunStreamingEnabled,
  publishAgentRunEvent,
  releaseAgentRunTerminalizationClaim,
  startAgentRun,
  tryClaimAgentRunTerminalization,
} from '../run/agent-run-store';
import { captureNoteGenerationFailed, tapFirstContent, type NoteGenerationFailureType } from '../note-generation-session-store';
import { createDocumentEditTarget, createTargetedEditTarget, type AgentEditTarget } from '../edit-target';
import { buildCompletionDocumentPatch } from '../run/completion-patch';
import { endEditorStreamingPreview } from '../request-editor';
import { getAgentRequestDocumentFields, getEditorCharCount } from '../document-utils';
import type { PersistedDocumentWriter } from '../review/review-session-types';
import { ServerStreamError, streamPromptMode } from './prompt-mode-client';
import { AiCreditsExhaustedError, AuthDeniedError } from './agent-fetch';
import { isLikelyNetworkError } from '../is-likely-network-error';

export type GenerateTextContextParams = {
  note?: RxDocument<Note>;
  block?: RxDocument<Block>;
  activeBlockId?: string;
};

export type GenerateTextSessionParams = GenerateTextContextParams & {
  userPrompt: string;
  includeContext?: boolean;
  userSource?: RxDocument<UserSource>;
  persistDocumentPatch?: PersistedDocumentWriter;
  editor?: Editor | null;
  position?: { from: number; to: number };
};

type GenerateTextRuntime = Pick<
  AgentLifecycleRuntime,
  'editor' | 'document' | 'persistDocumentPatch' | 'onEditPhaseFinish' | 'onFinish' | 'onError'
> & {
  getGenerateTextContext: (params: GenerateTextContextParams) => Promise<any>;
};

export function createGenerateTextAction({
  requestId,
  getAccessToken,
  resolveRuntime,
  beginAttempt,
  startPromptRequest,
  finishPromptRequest,
}: {
  requestId: string;
  getAccessToken: () => Promise<string | null>;
  resolveRuntime: () => GenerateTextRuntime | undefined;
  beginAttempt: (params: { runId: number; editTarget: AgentEditTarget }) => PreparedAgentAttempt | null;
  startPromptRequest: () => AbortController;
  finishPromptRequest: (abortController: AbortController) => void;
}): (params: GenerateTextSessionParams) => Promise<void> {
  return async ({
    userPrompt,
    includeContext = false,
    note,
    block,
    userSource,
    persistDocumentPatch,
    activeBlockId,
    editor,
    position,
  }: GenerateTextSessionParams): Promise<void> => {
    const runtime = resolveRuntime();

    const currentEditor = editor ?? runtime?.editor;
    if (!currentEditor) {
      return;
    }

    const document = note ?? block ?? userSource ?? runtime?.document;
    const documentWasExplicitlyPassed = !!(note || block || userSource);
    const documentPatchWriter = persistDocumentPatch ?? runtime?.persistDocumentPatch;
    if (documentWasExplicitlyPassed && !documentPatchWriter) {
      throw new Error('Missing persistDocumentPatch for persisted generateText request.');
    }

    const context =
      includeContext && (note || block) ? await runtime?.getGenerateTextContext({ note, block, activeBlockId }) : undefined;
    const { variables } = buildGenerateTextVariables(userPrompt, context);
    const userSources = serializeUserSources(context?.userSources);
    const editTarget = position ? createTargetedEditTarget(position) : createDocumentEditTarget();
    const runId = startAgentRun(requestId);
    const attempt = beginAttempt({ runId, editTarget });
    if (!attempt) return;

    const preparedEditor = attempt.editor;
    const { initialPos, safeFrom, safeTo } = getBlockRangeBoundaries(preparedEditor, position);
    const beforeDoc = preparedEditor.state.doc;
    const startedFromEmptyEditor = preparedEditor.isEmpty;
    const schemaAwareness = attempt.toolkit.getHtmlSchemaAwareness();
    const abortController = startPromptRequest();

    let hasError = false;
    // The first terminal (finish / cancel / error) to claim wins; later racing terminals must
    // no-op instead of double-publishing or reverting.
    const runGuard = createRunGuard(requestId, runId);

    const finalizeCancelledRun = ({ revert }: { revert: boolean }): void =>
      finalizeFailedRun({ source: 'cancel', event: { type: 'cancelled' }, errorType: 'abort_user', revert });

    const finalizeErroredRun = ({ network, revert }: { network: boolean; revert: boolean }): void =>
      finalizeFailedRun({
        source: 'error',
        event: { type: 'errored', network },
        errorType: network ? 'network' : 'unknown',
        revert,
      });

    // A request refused by the auth gate is not a failed generation: the session handles the denial
    // (agreements screen or sign-out), so the run ends as cancelled with no failure telemetry.
    const finalizeAuthDeniedRun = (): void =>
      finalizeFailedRun({ source: 'cancel', event: { type: 'cancelled' }, errorType: null, revert: false });

    // A request refused for AI credits is an expected billing state, not a failed generation: the
    // refusal rides on the cancelled event, and nothing was generated, so there is nothing to revert.
    const finalizeCreditsRefusedRun = (): void => {
      finalizeFailedRun({
        source: 'cancel',
        event: { type: 'cancelled', refusal: {} },
        errorType: null,
        revert: false,
      });
      capture('ai_credits_exhausted', { source: 'generate_text' });
    };

    // An edit too large to save is refused on purpose: it reverts and resolves without an error
    // callback, and the notifications show the content size limit toast for it.
    const finalizeContentTooLargeRun = (): void =>
      finalizeFailedRun({
        source: 'error',
        event: { type: 'errored', network: false, reason: 'content_too_large' },
        errorType: 'content_too_large',
        revert: true,
      });

    const finalizeFailedRun = ({
      source,
      event,
      errorType,
      revert,
    }: {
      source: 'cancel' | 'error';
      event: Extract<AgentRunEvent, { type: 'cancelled' | 'errored' }>;
      // null: not a failed generation, so no `note_generation_failed` is emitted.
      errorType: NoteGenerationFailureType | null;
      revert: boolean;
    }): void => {
      if (!tryClaimAgentRunTerminalization(requestId, runId, source)) return;
      clearRunScrollCleanup(requestId);
      if (revert && !preparedEditor.state.doc.eq(beforeDoc)) {
        preparedEditor.commands.revertToOriginalContent(beforeDoc, {
          to: safeTo,
          from: safeFrom,
        });
      }
      publishAgentRunEvent(requestId, runId, event);
      clearAgentRunEditSnapshot(requestId, runId);
      if (errorType) {
        captureNoteGenerationFailed({
          requestId,
          document,
          errorType,
          promptInputCharCount: userPrompt.length,
        });
      }
    };

    const finalizeGeneratedRun = async (usage: GenerationUsageMessageMetadata | undefined): Promise<void> => {
      endEditorStreamingPreview(requestId, preparedEditor);
      const hasEdit = !preparedEditor.state.doc.eq(beforeDoc);

      let resolution: CompletionResolution;
      let persistCompletionPatch: () => Promise<void>;
      if (startedFromEmptyEditor) {
        assertDocumentFitsTextBudget(preparedEditor.state.doc, getEditorHTML(preparedEditor));
        resolution = { outcome: 'direct_write' };
        persistCompletionPatch = () => {
          const completionDocumentPatch = buildCompletionDocumentPatch({
            requestId,
            document,
            editor: preparedEditor,
            outcome: 'direct_write',
          });
          if (completionDocumentPatch) {
            void documentPatchWriter?.(completionDocumentPatch);
          }
          return Promise.resolve();
        };
      } else {
        const pendingReview = documentPatchWriter
          ? prepareReview({ editor: preparedEditor, beforeDoc, configOptions: { showReplacement: false } })
          : null;
        resolution =
          pendingReview && documentPatchWriter
            ? await commitReviewOutcome({ requestId, runId, pendingReview, persistDocumentPatch: documentPatchWriter })
            : { outcome: 'write_finished' };
        persistCompletionPatch = () => Promise.resolve();
      }

      const finishResult = await finalizeSuccessfulAgentRun({
        requestId,
        runId,
        runGuard,
        editor: preparedEditor,
        document,
        resolution,
        awaitingPostEditSummary: false,
        hasEdit,
        persistCompletionPatch,
        capture: {
          outputCharCount: getEditorCharCount(preparedEditor),
          promptInputCharCount: userPrompt.length,
          targetedEdit: getAgentRunEditScope(requestId) === 'targeted',
          streamingEnabled: getAgentRunStreamingEnabled(requestId),
          usage,
        },
        onEditPhaseFinish: runtime?.onEditPhaseFinish,
        onFinish: runtime?.onFinish,
      });
      if (finishResult) {
        emitAgentRunFinished(finishResult);
      }
    };

    try {
      const { textStream, usage } = await streamPromptMode(
        'generate-text',
        {
          variables: {
            ...variables,
            schemaAwareness,
          },
          ...(userSources.length ? { userSources } : {}),
          ...getAgentRequestDocumentFields(document),
        },
        { getAccessToken, signal: abortController.signal }
      );

      publishAgentRunEvent(requestId, runId, { type: 'agent_write_started' });

      const editorStream = getAgentRunStreamingEnabled(requestId)
        ? tapFirstContent(requestId, textStream)
        : await bufferTextStreamToSingleChunk(textStream);

      await attempt.toolkit.streamHtml(editorStream, {
        position: initialPos,
        reviewOptions: { mode: 'disabled' },
        onError: ({ error }) => {
          hasError = true;
          endEditorStreamingPreview(requestId, preparedEditor);

          const runtimeError = error instanceof Error ? error : new Error(String(error));
          if (isAbortError(runtimeError) || isServerAbort(runtimeError)) {
            finalizeCancelledRun({ revert: true });
            return;
          }

          if (runtimeError instanceof ServerStreamError) {
            finalizeErroredRun({ network: false, revert: true });
            return;
          }

          finalizeErroredRun({ network: isLikelyNetworkError(runtimeError), revert: true });
        },
      });

      if (!hasError && tryClaimAgentRunTerminalization(requestId, runId, 'finish')) {
        try {
          await finalizeGeneratedRun(await usage);
        } catch (error) {
          // Release the finish claim so the error path below can still terminalize the run.
          releaseAgentRunTerminalizationClaim(requestId, runId, 'finish');
          throw error;
        }
      }
    } catch (error) {
      endEditorStreamingPreview(requestId, preparedEditor);
      const runtimeError = error instanceof Error ? error : new Error(String(error));
      // Checked first: the size check runs after streaming, so a later abort must not skip the revert.
      if (runtimeError instanceof ContentTooLargeError) {
        finalizeContentTooLargeRun();
        return;
      }

      if (abortController.signal.aborted || isAbortError(runtimeError) || isServerAbort(runtimeError)) {
        finalizeCancelledRun({ revert: false });
        return;
      }

      // Resolves rather than rethrows, like an abort: there is no failure for the caller to report.
      if (runtimeError instanceof AuthDeniedError) {
        finalizeAuthDeniedRun();
        return;
      }

      if (runtimeError instanceof AiCreditsExhaustedError) {
        finalizeCreditsRefusedRun();
        return;
      }

      if (runtimeError instanceof ServerStreamError) {
        finalizeErroredRun({ network: false, revert: false });
        runtime?.onError?.(runtimeError);
        throw runtimeError;
      }

      finalizeErroredRun({ network: isLikelyNetworkError(runtimeError), revert: false });
      runtime?.onError?.(runtimeError);
      throw runtimeError;
    } finally {
      finishPromptRequest(abortController);
    }
  };
}

async function bufferTextStreamToSingleChunk(textStream: ReadableStream<string>): Promise<ReadableStream<string>> {
  const reader = textStream.getReader();
  let buffered = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffered += value;
    }
  } finally {
    reader.releaseLock();
  }

  return new ReadableStream<string>({
    start(controller) {
      controller.enqueue(buffered);
      controller.close();
    },
  });
}

function isServerAbort(error: Error): boolean {
  return error instanceof ServerStreamError && error.origin === 'abort';
}
